import "./LineChart.css";

import { Scale, TooltipItem } from "chart.js";
import { Line } from "react-chartjs-2";

import { useChartVariant } from "../ChartVariantContext";
import { ChartDatasets } from "../models";
import { ChartVariant, maybeSetGlobalChartJsConfiguration } from "../utils";
import { useLineChart } from "./useLineChart";

maybeSetGlobalChartJsConfiguration();

interface LineChartProps {
  height: number;
  labels: string[];
  datasets: ChartDatasets;
  chartOffset?: number;
  customChartColours?: string[];
  emptyPlaceholder?: string;
  xAxisTickOffset?: number;
  xAxisTitle?: string;
  yAxisTitle?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  yAxisTickCallbackFunction?: (this: Scale, val: string | number, index: number) => any;
  labelCallbackFunction?: (tooltipItem: TooltipItem<"line">) => string;
  /**
   * Design-system brand: `"hub"` (Space Grotesk, tighter curves) or `"seed"` (Figtree, smoother).
   * Defaults to the nearest ChartVariantProvider (Hub if none).
   */
  variant?: ChartVariant;
}

export const LineChart = ({
  height,
  labels,
  datasets,
  chartOffset,
  customChartColours,
  emptyPlaceholder = "No data to display",
  xAxisTickOffset,
  xAxisTitle,
  yAxisTitle,
  yAxisTickCallbackFunction,
  labelCallbackFunction,
  variant,
}: LineChartProps): JSX.Element => {
  const contextVariant = useChartVariant();
  const { options, chartData, plugins, lineChartRef } = useLineChart({
    labels,
    datasets,
    chartOffset,
    customChartColours,
    xAxisTickOffset,
    xAxisTitle,
    yAxisTitle,
    yAxisTickCallbackFunction,
    labelCallbackFunction,
    variant: variant ?? contextVariant,
  });
  if ((!chartData && !plugins) || !labels)
    return (
      <div className="Line Line_empty">
        <p className="body3">{emptyPlaceholder}</p>
      </div>
    );

  return (
    <div className="Line">
      <Line data={chartData} options={options} plugins={plugins} ref={lineChartRef} height={height} />
    </div>
  );
};
